import { useEffect, useState } from 'react';
import { Modal, Button, RadioGroup, Select, Alert, Space, Statistic, Row, Col, Spinner, message } from '../../ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useConfirmedMutation } from '../../lib/confirm';
import { request } from '../../api/client';
import { requestAllPages } from '../../api/allPages';

interface Props {
  open: boolean;
  onClose: () => void;
  /** The user being deactivated (id, fullName, role). */
  user: any;
}

/**
 * Deactivate a staff user. For a field user with territories/customers, the operator
 * decides their book's fate: hand everything to a replacement Sales Person, or release
 * the territories into the unassigned pool for later reassignment.
 */
export function DeactivateUserModal({ open, onClose, user }: Props) {
  const qc = useQueryClient();
  const [mode, setMode] = useState<'reassign' | 'release'>('release');
  const [toUserId, setToUserId] = useState<string | undefined>();

  // The users list carries role as a nested Role object (role.code), not a bare string.
  const roleCode: string | undefined = user?.role?.code ?? (typeof user?.role === 'string' ? user.role : undefined);
  const isSp = roleCode === 'SALES_PERSON';
  const isSh = roleCode === 'SALES_HEAD';

  const { data: footprint, isLoading } = useQuery({
    queryKey: ['user-footprint', user?.id],
    queryFn: () => request<{ territories: number; retailers: number; farmers: number }>({ url: `/territory-assignments/user/${user.id}/footprint` }),
    enabled: open && !!user?.id,
  });
  const { data: spUsers } = useQuery({
    queryKey: ['sp-list-all'],
    queryFn: () => requestAllPages('/users', { role: 'SALES_PERSON' }),
    enabled: open && isSp,
  });
  const replacementOptions = (spUsers?.data ?? [])
    .filter((u: any) => String(u.id) !== String(user?.id) && u.status === 'active')
    .map((u: any) => ({ value: String(u.id), label: u.fullName }));

  useEffect(() => {
    if (open) { setMode('release'); setToUserId(undefined); }
  }, [open]);

  const hasBook = !!footprint && (footprint.territories > 0 || footprint.retailers > 0 || footprint.farmers > 0);

  const deactivate = useConfirmedMutation({ title: "Deactivate this user?", content: "They are signed out and can no longer log in. Their territories and customers are released as chosen above.", okText: "Yes, deactivate", danger: true }, {
    mutationFn: () => request({
      url: `/users/${user.id}`,
      method: 'DELETE',
      params: mode === 'reassign' && toUserId ? { reassignTo: toUserId } : undefined,
    }),
    onSuccess: (res: any) => {
      const moved = res?.movedCustomers ?? 0;
      message.success(
        mode === 'reassign' && toUserId
          ? `Deactivated — territories + ${moved} customer(s) handed over`
          : `Deactivated — territories released; ${moved} customer(s) re-pointed by territory`,
      );
      qc.invalidateQueries({ queryKey: ['users'] });
      qc.invalidateQueries({ queryKey: ['territory-coverage'] });
      qc.invalidateQueries({ queryKey: ['badges'] });
      onClose();
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed to deactivate'),
  });

  const okDisabled = isSp && hasBook && mode === 'reassign' && !toUserId;

  return (
    <Modal
      open={open}
      onOpenChange={(o) => { if (!o) onClose(); }}
      title={`Deactivate ${user?.fullName ?? 'user'}?`}
      footer={
        <>
          <Button variant="default" onClick={onClose}>Cancel</Button>
          <Button variant="danger" loading={deactivate.isPending} disabled={okDisabled} onClick={() => deactivate.mutate()}>Deactivate</Button>
        </>
      }
    >
      <Alert type="warning" className="mb-3.5"
        title="They will no longer be able to log in until reactivated. Their order & visit history is kept." />

      {isLoading ? (
        <Spinner />
      ) : isSp || isSh ? (
        <>
          <Row gutter={12} style={{ marginBottom: 14 }}>
            <Col span={8}><Statistic title="Territories" value={footprint?.territories ?? 0} /></Col>
            <Col span={8}><Statistic title="Retailers" value={footprint?.retailers ?? 0} /></Col>
            <Col span={8}><Statistic title="Farmers" value={footprint?.farmers ?? 0} /></Col>
          </Row>

          {!hasBook ? (
            <Alert type="info" title={`This ${isSh ? 'sales head' : 'sales person'} has no territories or customers to hand over.`} />
          ) : isSp ? (
            <Space direction="vertical" style={{ width: '100%' }}>
              <RadioGroup
                value={mode}
                onChange={(value) => setMode(value as 'reassign' | 'release')}
                options={[
                  { value: 'reassign', label: 'Hand territories & customers to a replacement Sales Person' },
                  { value: 'release', label: "Release territories — customers move to each territory's next owner, else the unassigned pool" },
                ]}
              />
              {mode === 'reassign' && (
                <Select
                  className="w-full mt-1"
                  placeholder="Select the replacement sales person"
                  value={toUserId} onChange={setToUserId}
                  options={replacementOptions}
                />
              )}
            </Space>
          ) : (
            // Sales Head: oversight bindings (zone/state/district) can't be handed to an SP.
            <Alert type="warning"
              title={`This sales head's ${footprint?.territories ?? 0} oversight binding(s) will be released. Sales-head territories can't be handed to a sales person.`} />
          )}
        </>
      ) : (
        <Alert type="info" title="This user has no field territory to release." />
      )}
    </Modal>
  );
}
